import React, { useState, useEffect } from "react";
import { X, Wifi, WifiOff, RefreshCw, Home, AlertCircle } from "lucide-react";
import { router } from "@inertiajs/react";

interface OfflineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRetry?: () => void;
  title?: string;
  message?: string;
  showRetryButton?: boolean;
  showHomeButton?: boolean;
  autoRetry?: boolean;
  retryInterval?: number;
}

const OfflineModal: React.FC<OfflineModalProps> = ({
  isOpen,
  onClose,
  onRetry,
  title = "Koneksi Terputus",
  message = "Sepertinya Anda tidak terhubung ke internet saat ini. Beberapa fitur dan konten terbaru mungkin tidak tersedia.",
  showRetryButton = true,
  showHomeButton = true,
  autoRetry = true,
  retryInterval = 5000,
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [retryCount, setRetryCount] = useState(0);
  const [autoRetryCountdown, setAutoRetryCountdown] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  // Check online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setRetryCount(0);
      setAutoRetryCountdown(0);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Auto retry functionality
  useEffect(() => {
    if (!isOpen || !autoRetry || isOnline) return;

    const countdown = setInterval(() => {
      setAutoRetryCountdown((prev) => {
        if (prev <= 1) {
          handleRetry();
          return Math.floor(retryInterval / 1000);
        }
        return prev - 1;
      });
    }, 1000);

    // Initial countdown
    setAutoRetryCountdown(Math.floor(retryInterval / 1000));

    return () => clearInterval(countdown);
  }, [isOpen, autoRetry, retryInterval, isOnline]);

  // Auto close when back online
  useEffect(() => {
    if (isOnline && isOpen) {
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  }, [isOnline, isOpen, onClose]);

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryCount((prev) => prev + 1);

    try {
      // Test connection by fetching a small resource
      const response = await fetch("/favicon.ico", {
        method: "HEAD",
        cache: "no-cache",
      });

      if (response.ok) {
        setIsOnline(true);
        if (onRetry) {
          onRetry();
        } else {
          // Default retry behavior - reload page
          window.location.reload();
        }
      }
    } catch (error) {
      console.warn("Retry failed:", error);
      // Keep modal open, auto retry will continue
    } finally {
      setIsRetrying(false);
    }
  };

  const handleGoHome = () => {
    onClose();
    router.visit("/");
  };

  const handleClose = () => {
    setRetryCount(0);
    setAutoRetryCountdown(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-gray-100 p-2 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-200"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-pink-600 px-6 py-8 text-center text-white">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
            {isOnline ? (
              <Wifi className="h-8 w-8 text-green-300" />
            ) : (
              <WifiOff className="h-8 w-8" />
            )}
          </div>
          <h2 className="text-xl font-bold">
            {isOnline ? "Koneksi Pulih!" : title}
          </h2>
          <div className="mt-2 text-sm text-white/90">
            Lambe Turah News
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isOnline ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <Wifi className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Koneksi internet telah pulih. Halaman akan dimuat ulang
                otomatis.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-300">{message}</p>
              </div>

              {/* Retry info */}
              {retryCount > 0 && (
                <div className="mb-4 rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Percobaan ke: {retryCount}
                  </p>
                  {autoRetry && autoRetryCountdown > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Coba lagi otomatis dalam {autoRetryCountdown} detik
                    </p>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="space-y-3">
                {showRetryButton && (
                  <button
                    onClick={handleRetry}
                    disabled={isRetrying}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 px-4 py-3 font-medium text-white transition-all hover:from-red-600 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`}
                    />
                    {isRetrying ? "Mencoba..." : "Coba Lagi"}
                  </button>
                )}

                {showHomeButton && (
                  <button
                    onClick={handleGoHome}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-all hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    <Home className="h-4 w-4" />
                    Kembali ke Beranda
                  </button>
                )}
              </div>

              {/* Tips */}
              <div className="mt-6 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <h4 className="mb-2 font-medium text-blue-900 dark:text-blue-300">
                  Tips:
                </h4>
                <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-400">
                  <li>• Periksa koneksi internet Anda</li>
                  <li>• Coba refresh halaman (Ctrl+F5)</li>
                  <li>• Beberapa konten mungkin tersedia offline</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 text-center dark:border-gray-700 dark:bg-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            © Lambe Turah News - Semua konten yang telah Anda kunjungi
            sebelumnya masih tersedia secara offline.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OfflineModal;
